import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import Keycloak from 'keycloak-js';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root',
})
export class KeycloakService {
  private _keycloak: Keycloak | undefined;
  keycloakInstance: Keycloak.KeycloakInstance;
  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  get Keycloak(): Keycloak {
    if (!this._keycloak && isPlatformBrowser(this.platformId)) {
      this._keycloak = new Keycloak({
        url: environment.auth,
        realm: environment.realm,
        clientId: 'VIBRATIONS_FRONT',
      });
    }
    return this._keycloak!;
  }

  async init(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      const authenticated = await this.Keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html'

      });

      if (!authenticated) {
        await this.Keycloak.login(); 
      }
      this.keycloakInstance = this.Keycloak;
      console.log('Authenticated:', authenticated);
    }
  }

  async login(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {
      await this.Keycloak.login();
    }
  }

  get userId(): string {
    if (isPlatformBrowser(this.platformId)) {
      return this.Keycloak?.tokenParsed?.sub as string;
    }
    return '';
  }

  get isTokenValid(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      return !this.Keycloak.isTokenExpired();
    }
    return false;
  }

  get fullName(): string {
    if (isPlatformBrowser(this.platformId)) {
      return this.Keycloak?.tokenParsed?.['name'] as string;
    }
    return '';
  }

  get email(): string {
    if (isPlatformBrowser(this.platformId)) {
      return this.Keycloak?.tokenParsed?.['email'] as string;
    }
    return '';
  }

  logout(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.Keycloak.logout({ redirectUri: window.location.origin });
    }
  }

  accountManagement(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.Keycloak.accountManagement();
    }
  }
    /**
   * Get all user roles (realm + resource roles combined and deduplicated)
   */
  public get allRoles(): string[] {
    const realmRoles = this.keycloakInstance.tokenParsed?.realm_access?.roles ?? [];
    const resourceRoles = Object.values(this.keycloakInstance.tokenParsed?.resource_access ?? {})
      .flatMap((resource: any) => resource.roles ?? []);
    return [...new Set([...realmRoles, ...resourceRoles])];
  }

    /**
   * Check if user has a specific role (realm or resource role)
   * This is the centralized method for role checking - use this instead of
   * direct Keycloak hasRealmRole/hasResourceRole calls.
   */
  public hasRole(role: string): boolean {
    return this.allRoles.includes(role);
  }
    /**
   * Check if user has any of the specified roles
   */
  public hasAnyRole(roles: string[]): boolean {
    return roles.some(role => this.allRoles.includes(role));
  }
}
